/* ==========================================================================
   WARMY - CHROME
   Mobile menu, offer countdown, newsletter slides, moments timeline, the cart
   drawer (rendered from the cart API - real state only), and live Kaching set
   pricing for homepage tiles.

   ONE FIVE-RUNG OFFER LADDER drives the progress copy, the upsell and the
   shipping row, so they can never contradict each other:
     empty       invite the first hoodie - never "add N more", which read as
                 nonsense on an empty cart
     tier1       chase the Duo, naming the live dollar saving
     tier2       Duo celebrated, chase the free 4th
     tier2_last  exactly one away: badge escalates, copy talks about the free one
     complete    congratulate and stop selling - upsell removed entirely
   Savings figures come from the Kaching deal itself when its API is present,
   so the copy cannot drift from what checkout actually charges.
   ========================================================================== */
(() => {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CURRENCY = () => (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

  const money = (cents, currency) => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || CURRENCY() }).format(cents / 100); }
    catch { return '$' + (cents / 100).toFixed(2); }
  };

  /* --- Kaching deal reader -------------------------------------------------- */
  const kaching = (() => {
    const cache = new Map();
    const ready = () => !!(window.kachingBundlesApi && typeof window.kachingBundlesApi.fetchDeals === 'function');
    const waitFor = (ms = 10000) => new Promise((resolve) => {
      if (ready()) return resolve(true);
      let waited = 0;
      const t = setInterval(() => {
        waited += 250;
        if (ready() || waited >= ms) { clearInterval(t); resolve(ready()); }
      }, 250);
    });
    const config = () => {
      try {
        const cfg = document.getElementById('kaching-bundles-config');
        if (cfg) return JSON.parse(cfg.textContent) || {};
      } catch { /* default */ }
      return {};
    };
    const rate = () => Number(config().currencyRate) || 1;
    const format = (cents) => {
      const f = config().moneyFormat;
      if (typeof f !== 'string' || !f.includes('{{')) return money(cents);
      const amount = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return f.replace(/\{\{\s*[^}]+\s*\}\}/g, amount).replace(/<[^>]*>/g, '');
    };
    const deals = async (productId) => {
      if (!cache.has(productId)) {
        cache.set(productId, (await waitFor()) ? window.kachingBundlesApi.fetchDeals({ productId }) : Promise.resolve([]));
      }
      return (await cache.get(productId)) || [];
    };
    const barFor = async (productId, dealId, qty) => {
      const all = await deals(productId);
      const deal = (dealId && all.find((d) => d.nanoId === dealId)) || all[0];
      const bars = (deal && deal.dealBars) || [];
      return bars
        .filter((b) => b.dealBarType === 'quantity-break' && b.quantity <= qty && b.discount && b.discount.value)
        .sort((a, b) => b.quantity - a.quantity)[0] || null;
    };
    const perUnitCents = (bar, baseCents) => {
      if (!bar) return baseCents;
      return bar.discount.type === 'percentage'
        ? Math.round(baseCents * (1 - bar.discount.value / 100))
        : Math.max(0, baseCents - Math.round(bar.discount.value * rate() * 100));
    };
    const savingsCents = async (productId, dealId, baseCents, qty) => {
      const bar = await barFor(productId, dealId, qty);
      if (!bar) return 0;
      return Math.max(0, (baseCents - perUnitCents(bar, baseCents)) * qty);
    };
    return { deals, barFor, perUnitCents, savingsCents, format, rate };
  })();

  /* --- Mobile menu ------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-menu-toggle]');
    if (!toggle) return;
    const menu = document.querySelector('[data-site-menu]');
    if (!menu) return;
    const open = menu.hidden;
    menu.hidden = !open;
    document.body.classList.toggle('menu-open', open);
    toggle.setAttribute('aria-expanded', String(open));
  });

  /* --- Offer countdown ------------------------------------------------------ */
  document.querySelectorAll('[data-offer-timer]').forEach((el) => {
    const fixedEnd = el.dataset.end ? Date.parse(el.dataset.end) : NaN;
    const pad = (n) => String(n).padStart(2, '0');
    const endOfDay = () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d.getTime(); };
    const tick = () => {
      const end = Number.isNaN(fixedEnd) ? endOfDay() : fixedEnd;
      const left = end - Date.now();
      if (left <= 0) {
        if (!Number.isNaN(fixedEnd)) { el.textContent = el.dataset.expired || ''; clearInterval(t); }
        return;
      }
      const s = Math.floor(left / 1000);
      const d = Math.floor(s / 86400);
      const clock = pad(Math.floor(s % 86400 / 3600)) + ':' + pad(Math.floor(s % 3600 / 60)) + ':' + pad(s % 60);
      el.textContent = d > 0 ? d + 'd ' + clock : clock;
    };
    tick();
    const t = setInterval(tick, 1000);
  });

  /* --- Newsletter slides --------------------------------------------------- */
  document.querySelectorAll('[data-newsletter]').forEach((root) => {
    const slides = [...root.querySelectorAll('.home-newsletter-slide')];
    const dots = [...root.querySelectorAll('[data-newsletter-slide]')];
    if (slides.length < 2) return;
    let current = 0;
    const show = (i) => {
      current = (i + slides.length) % slides.length;
      slides.forEach((s, n) => s.classList.toggle('is-active', n === current));
      dots.forEach((d, n) => d.setAttribute('aria-pressed', String(n === current)));
    };
    dots.forEach((d) => d.addEventListener('click', () => { show(Number(d.dataset.newsletterSlide)); reset(); }));
    let timer = setInterval(() => show(current + 1), 5000);
    const reset = () => { clearInterval(timer); timer = setInterval(() => show(current + 1), 5000); };
  });

  /* --- Moments timeline ----------------------------------------------------- */
  const timeline = document.querySelector('[data-moment-timeline]');
  if (timeline) {
    const steps = [...timeline.querySelectorAll('.moment-step')];
    const update = () => {
      const rect = timeline.getBoundingClientRect();
      const vh = innerHeight;
      const total = rect.height - vh * 0.5;
      const passed = Math.min(Math.max(vh * 0.5 - rect.top, 0), total);
      timeline.style.setProperty('--moment-progress', (total > 0 ? (passed / total) * 100 : 0) + '%');
      steps.forEach((step) => {
        const r = step.getBoundingClientRect();
        step.querySelector('.moment-node')?.classList.toggle('is-active', r.top < vh * 0.55);
      });
    };
    addEventListener('scroll', update, { passive: true });
    update();
  }

  /* --- Variant data for the drawer upsell ----------------------------------- */
  const variantData = (() => {
    const node = document.querySelector('[data-warmy-product-data]') || document.querySelector('[data-warmy-upsell-data]');
    if (!node) return null;
    try {
      const d = JSON.parse(node.textContent);
      if (!d || !Array.isArray(d.variants) || !d.variants.length) return null;
      if (!d.optionIndex || d.optionIndex.color == null || d.optionIndex.size == null) return null;
      return d;
    } catch { return null; }
  })();

  /* --- Cart drawer ------------------------------------------------------------ */
  const drawer = document.querySelector('[data-cart-drawer]');
  const overlay = document.querySelector('[data-cart-overlay]');
  const upsell = drawer ? drawer.querySelector('[data-cart-upsell]') : null;

  const tiers = () => {
    const p = document.querySelector('[data-cart-progress]');
    const t1 = Number(p?.dataset.tier1Qty || 0);
    const t2 = Number(p?.dataset.tier2Qty || p?.dataset.progressTarget || upsell?.dataset.upsellTarget || 4);
    return { t1, t2 };
  };

  /* Which rung of the ladder a given cart size sits on. */
  const ladderState = (count, t1, t2) => {
    if (count === 0) return { state: 'empty', remaining: t1, target: t1 };
    if (t1 && count < t1) return { state: 'tier1', remaining: t1 - count, target: t1 };
    if (count < t2) {
      const remaining = t2 - count;
      return { state: remaining === 1 ? 'tier2_last' : 'tier2', remaining, target: t2 };
    }
    return { state: 'complete', remaining: 0, target: t2 };
  };

  /* Units of the bundle product only - accessories (socks, blanket, joggers) must
     never advance the offer ladder or its savings promises. Falls back to the whole
     cart only when the upsell product data is absent. */
  const bundleUnits = (cart) => {
    if (!variantData || !Array.isArray(variantData.variants)) return cart.item_count;
    const ids = new Set(variantData.variants.map((v) => v.id));
    return cart.items.reduce((s, i) => s + (ids.has(i.variant_id) ? i.quantity : 0), 0);
  };

  /* The store's configured free-shipping threshold (cart_free_shipping_threshold, $75).
     Shopify grants it by discounted cart value, so the flag must key off price - item
     count made two $29.95 socks read as FREE. */
  const FREE_SHIPPING_CENTS = 7500;

  const renderShipping = (cart) => {
    document.querySelectorAll('[data-cart-shipping]').forEach((el) => {
      if (!el.dataset.paidText) el.dataset.paidText = el.dataset.defaultText || 'Calculated at checkout';
      const freeText = el.dataset.freeText || 'FREE';
      const qualifies = cart.total_price >= FREE_SHIPPING_CENTS;
      el.textContent = qualifies ? freeText : el.dataset.paidText;
      el.classList.toggle('is-free', qualifies);
    });
  };

  const renderUpsell = (cart) => {
    if (!upsell) return;
    const { t1, t2 } = tiers();
    const count = bundleUnits(cart);
    const { state, remaining } = ladderState(count, t1, t2);
    if (!variantData || state === 'complete') { upsell.hidden = true; return; }

    const ci = variantData.optionIndex.color;
    const si = variantData.optionIndex.size;
    const avail = variantData.variants.filter((v) => v.available);
    if (!avail.length) { upsell.hidden = true; return; }
    const colors = [...new Set(avail.map((v) => v.options[ci]))];
    const sizes = [...new Set(avail.map((v) => v.options[si]))];

    const d = upsell.dataset;
    const copy = {
      empty: { badge: d.emptyBadge, heading: d.emptyHeading, body: d.emptyBody, rows: 1 },
      tier1: { badge: d.t1Badge, heading: d.t1Heading, body: d.t1Body, rows: remaining },
      tier2: { badge: d.t2Badge, heading: d.t2Heading, body: d.t2Body, rows: remaining },
      tier2_last: { badge: d.lastBadge, heading: d.lastHeading, body: d.lastBody, rows: remaining }
    }[state];
    if (!copy) { upsell.hidden = true; return; }

    const put = (sel, text) => {
      const el = upsell.querySelector(sel);
      if (el && text != null) el.textContent = String(text).replace(/\[count\]/g, remaining);
    };
    put('[data-cart-upsell-tag]', copy.badge);
    put('[data-cart-upsell-heading]', copy.heading);
    put('[data-cart-upsell-body]', copy.body);

    const rows = copy.rows;
    const box = upsell.querySelector('[data-cart-upsell-options]');
    if (!box || rows < 1) { upsell.hidden = true; return; }
    if (Number(box.dataset.rows) !== rows || Number(box.dataset.from) !== count) {
      box.dataset.rows = rows;
      box.dataset.from = count;
      const opts = (values, sel) => values.map((v) => `<option value="${v.replaceAll('"', '&quot;')}"${v === sel ? ' selected' : ''}>${v}</option>`).join('');
      box.innerHTML = Array.from({ length: rows }, (_, n) => `
        <div class="cart-upsell-row">
          <strong>Warmy ${count + n + 1}</strong>
          <label class="cart-upsell-field"><span>Color</span><select data-upsell-color>${opts(colors, colors[(count + n) % colors.length])}</select></label>
          <label class="cart-upsell-field"><span>Size</span><select data-upsell-size>${opts(sizes, sizes.includes('M') ? 'M' : sizes[0])}</select></label>
        </div>`).join('');
    }
    upsell.hidden = false;
  };

  const renderProgress = (cart) => {
    document.querySelectorAll('[data-cart-progress]').forEach(async (progress) => {
      const d = progress.dataset;
      const t1 = Number(d.tier1Qty || 0);
      const t2 = Number(d.tier2Qty || d.progressTarget || 4);
      const count = bundleUnits(cart);
      const { state, remaining, target } = ladderState(count, t1, t2);

      const fill = progress.querySelector('[data-cart-progress-fill]');
      if (fill) fill.style.width = Math.min(count / t2, 1) * 100 + '%';
      progress.querySelectorAll('[data-cart-progress-slot]').forEach((slot, n) => {
        slot.classList.toggle('is-filled', n < count);
      });

      const msgEl = progress.querySelector('[data-cart-progress-message]');
      if (!msgEl) return;

      const template = {
        empty: d.emptyMsg,
        tier1: d.tier1Msg,
        tier2: d.tier2Msg,
        tier2_last: d.tier2LastMsg,
        complete: d.completeMsg
      }[state];
      if (!template) return;
      const fallback = (state === 'empty' || state === 'tier1') ? (d.tier1Savings || '') : (d.tier2Savings || '');

      const write = (savingsText) => {
        msgEl.textContent = template.replace(/\[count\]/g, remaining).replace(/\[savings\]/g, savingsText);
      };
      write(fallback);

      const productId = Number(d.productId);
      const baseCents = Number(d.baseCents);
      if (!productId || !baseCents || !template.includes('[savings]')) return;
      try {
        const cents = await kaching.savingsCents(productId, d.dealId, baseCents, target);
        if (cents > 0) write(kaching.format(cents));
      } catch { /* keep the fallback */ }
    });
  };

  const renderCart = async (cart) => {
    if (!cart) cart = await (await fetch(ROOT + 'cart.js')).json();
    const cur = cart.currency;

    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    renderProgress(cart);
    renderShipping(cart);
    if (!drawer) return cart;

    const titleCount = drawer.querySelector('[data-cart-title-count]');
    if (titleCount) titleCount.textContent = cart.item_count;

    const lines = drawer.querySelector('[data-cart-lines]');
    if (lines) {
      lines.innerHTML = cart.items.length ? cart.items.map((i) => `
        <div class="cart-line" data-line-key="${i.key}">
          <img src="${i.image ? i.image.replace(/(\.[a-z]+)(\?|$)/, '_120x$1$2') : ''}" alt="" width="60" height="60">
          <div class="cart-line-copy"><strong>${i.product_title}</strong><span>${i.variant_title || ''}</span></div>
          <div class="cart-line-side">
            <span class="cart-line-price">${i.original_line_price !== i.final_line_price ? '<s style=\"color:var(--muted);font-weight:500\">' + money(i.original_line_price, cur) + '</s> ' : ''}${money(i.final_line_price, cur)}</span>
            <span class="cart-line-quantity">
              <button type="button" data-cart-qty="-1" aria-label="Decrease quantity">&#8722;</button>
              <span>${i.quantity}</span>
              <button type="button" data-cart-qty="1" aria-label="Increase quantity">+</button>
            </span>
          </div>
        </div>`).join('') : '<p class="cart-empty">Your cart is empty.</p>';
    }

    const subtotal = drawer.querySelector('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.original_total_price, cur);
    const savingsRow = drawer.querySelector('[data-cart-savings-row]');
    const savings = drawer.querySelector('[data-cart-savings]');
    if (savingsRow && savings) {
      savingsRow.hidden = cart.total_discount <= 0;
      savings.textContent = '−' + money(cart.total_discount, cur);
    }
    const total = drawer.querySelector('[data-cart-total]');
    if (total) total.textContent = money(cart.total_price, cur);

    renderUpsell(cart);
    return cart;
  };

  const openDrawer = async () => {
    /* Parity build: the warmy drawer markup is not rendered (Impact chrome +
       cart_type "page", same as the live theme). When an add still lands here
       (warmy PDP builder, homepage quick add), fall back to the live theme's
       behaviour: go to the cart page. */
    if (!drawer) { window.location.href = '/cart'; return; }
    await renderCart();
    drawer.hidden = false;
    if (overlay) overlay.hidden = false;
    document.body.classList.add('cart-open');
  };
  const closeDrawer = () => {
    if (!drawer) return;
    drawer.hidden = true;
    if (overlay) overlay.hidden = true;
    document.body.classList.remove('cart-open');
  };

  const changeLine = async (key, quantity) => {
    await fetch(ROOT + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
    if (document.querySelector('[data-warmy-cart-page]')) location.reload();
    else document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };

  document.addEventListener('click', async (e) => {
    if (e.target.closest('[data-open-cart]')) { e.preventDefault(); openDrawer(); return; }
    if (e.target.closest('[data-close-cart]') || e.target.closest('[data-cart-overlay]')) { closeDrawer(); return; }

    const qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn) {
      const line = qtyBtn.closest('[data-line-key]');
      if (!line) return;
      const current = Number(line.querySelector('.cart-line-quantity span, .cart-line-quantity > span')?.textContent || qtyBtn.parentElement.querySelector('span').textContent);
      await changeLine(line.dataset.lineKey, Math.max(0, current + Number(qtyBtn.dataset.cartQty)));
      return;
    }

    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      const line = removeBtn.closest('[data-line-key]');
      if (line) await changeLine(line.dataset.lineKey, 0);
      return;
    }

    const upsellBtn = e.target.closest('[data-cart-upsell-add]');
    if (upsellBtn && upsell && variantData) {
      const ci = variantData.optionIndex.color;
      const si = variantData.optionIndex.size;
      const items = [...upsell.querySelectorAll('.cart-upsell-row')].map((row) => {
        const color = row.querySelector('[data-upsell-color]')?.value;
        const size = row.querySelector('[data-upsell-size]')?.value;
        const match = variantData.variants.find((v) => v.available && v.options[ci] === color && v.options[si] === size);
        return match ? { id: match.id, quantity: 1 } : null;
      }).filter(Boolean);
      const label = upsellBtn.textContent;
      if (!items.length) {
        upsellBtn.textContent = 'That combo is unavailable';
        setTimeout(() => { upsellBtn.textContent = label; }, 1600);
        return;
      }
      upsellBtn.disabled = true;
      upsellBtn.textContent = 'Adding…';
      try {
        const res = await fetch(ROOT + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
        if (!res.ok) throw new Error('add failed');
        if (document.querySelector('[data-warmy-cart-page]')) { location.reload(); return; }
        await renderCart();
      } catch {
        upsellBtn.textContent = 'Please try again';
      } finally {
        upsellBtn.disabled = false;
        setTimeout(() => { upsellBtn.textContent = label; }, 1200);
      }
    }
  });

  document.documentElement.addEventListener('cart:refresh', () => renderCart());
  document.addEventListener('warmy:added', () => openDrawer());
  /* Impact's product forms (every legacy template) announce a successful add with
     cart:change on documentElement. Nothing listened, so adds on those pages were
     silent - badge frozen, no drawer. Mirror the warmy add feedback. */
  document.documentElement.addEventListener('cart:change', () => openDrawer());
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closeDrawer();
    const menu = document.querySelector('[data-site-menu]');
    if (menu && !menu.hidden) {
      menu.hidden = true;
      document.body.classList.remove('menu-open');
      document.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
    }
  });

  renderCart();

  /* --- Live Kaching set pricing for homepage tiles --------------------------- */
  const setTiles = [...document.querySelectorAll('[data-warmy-set-price]')];
  if (setTiles.length) {
    (async () => {
      for (const tile of setTiles) {
        const productId = Number(tile.dataset.productId);
        const count = Number(tile.dataset.setCount);
        const base = Number(tile.dataset.baseCents);
        if (!productId || !count || !base) continue;
        try {
          const bar = await kaching.barFor(productId, tile.dataset.dealId, count);
          if (!bar) continue;
          const perUnit = kaching.perUnitCents(bar, base);
          const value = tile.querySelector('[data-warmy-set-price-value]');
          const compare = tile.querySelector('[data-warmy-set-compare]');
          if (value) value.textContent = kaching.format(perUnit * count);
          if (compare) compare.textContent = kaching.format(base * count);
        } catch { /* leave the Liquid fallback in place */ }
      }
    })();
  }
})();
